import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import PackOpeningModal from "./PackOpeningModal";
import TokenRewardModal from "./TokenRewardModal";
import { doc, updateDoc, getDoc, setDoc, increment } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import gameMenuBg from "../@shared/assets/game_menu.png";
import rewardIcon from "../@shared/assets/icons/reward_icon.png";
import tasksIcon from "../@shared/assets/icons/tasks_icon.png";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { Close } from "@mui/icons-material";
import { MainContext } from "../App";
import CardRewardModal from "./CardRewardModal";
import { checkAndCompleteQuests } from "../@shared/utils/Utils";

const dummyPoints = {
  damageDealtPoints: 100,
  eliminationPoints: 50,
  placementPoints: 300,
  playedCardPoints: 100,
  discardCardPoints: 50,
  tradeCardPoints: 75,
};

// Styles
const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalWrapperStyle = {
  width: "90vw",
  maxWidth: "600px",
  aspectRatio: 4 / 5,
  borderRadius: "16px",
  position: "relative",
};

const modalContentStyle = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "5% 8% 12%",
  backgroundImage: `url(${gameMenuBg})`,
  backgroundSize: "cover",
  position: "relative",
};

const questRow = {
  marginBottom: 16,
  backgroundColor: "#452098",
  padding: 12,
  borderRadius: 10,
};

const scrollAreaStyle = {
  marginTop: "10px",
  overflowY: "auto",
  flex: 1,
};

const rewardRowStyle = {
  marginBottom: "12px",
  backgroundColor: "#452098",
  padding: "12px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
};

export default function MyQuestsModal({ visible, onClose }) {
  const { userData } = useAuthStore();
  const { Theme } = useContext(MainContext);

  const activeQuests = userData?.quests || [];
  const logs = userData?.questLog || [];

  // ✅ Progress calculation
  const getQuestProgress = (quest) => {
    let progress = 0;

    logs.forEach((log) => {
      const stats = log.questStats || {};

      switch (quest.type) {
        case "damage":
          progress += stats.totalDamageDealt || 0;
          break;

        case "playCards":
          progress += stats.totalCardsPlayed || 0;
          break;

        case "cardType":
          progress +=
            stats.typesOfCardsPlayed?.filter((type) => type === quest.subtype)
              .length || 0;
          break;

        case "elementWins":
          if (
            stats.matchWon &&
            stats.characterElement?.toLowerCase() ===
              quest.subtype?.toLowerCase()
          ) {
            progress += 1;
          }
          break;

        case "rarityWins":
          if (
            stats.matchWon &&
            stats.characterRarity?.toLowerCase() ===
              quest.subtype?.toLowerCase()
          ) {
            progress += 1;
          }
          break;

        default:
          break;
      }
    });

    return progress;
  };

  const CustomProgressBar = ({ progress, total }) => {
    const percentage = Math.floor((progress / total) * 100);
    return (
      <Box
        style={{
          width: "100%",
          backgroundColor: "#553097",
          borderRadius: 6,
          overflow: "hidden",
          height: 8,
          marginTop: 6,
        }}
      >
        <Box
          style={{
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: "#13ffaf",
            height: 8,
          }}
        />
      </Box>
    );
  };

  const renderQuest = (quest) => {
    const progress = getQuestProgress(quest);
    const complete = progress >= quest.goal;

    const handleComplete = async () => {
      await checkAndCompleteQuests(userData);
    };

    return (
      <Box key={quest.id} style={questRow}>
        <Box style={{ flex: 1 }}>
          <Typography color="#fff" fontWeight="700" fontSize={14} mb={2}>
            {quest.title}
          </Typography>
          <Typography color="#ccc" fontSize={12}>
            {quest.description}
          </Typography>

          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: 5,
              borderRadius: 5,
              backgroundColor: "#ffffff40",
            }}
          >
            <img
              src={require("../@shared/assets/icons/reward_icon.png")}
              style={{ width: 20, height: 20 }}
            />
            <Typography color="#fee399" fontSize={13}>
              {quest.reward.xp} XP
            </Typography>
            <img
              src={require("../@shared/assets/infift_token.png")}
              style={{ width: 20, height: 20, marginLeft: 5 }}
            />
            <Typography color="#fee399" fontSize={13}>
              {quest.reward.tokens}
            </Typography>
          </Box>

          <CustomProgressBar progress={progress} total={quest.goal} />

          <Typography color={complete ? "#0f0" : "#aaa"} mt={4}>
            {progress > quest.goal ? quest.goal : progress}/{quest.goal}{" "}
            {complete ? "(Complete!)" : ""}
          </Typography>

          {complete && !quest.completed && (
            <NeoButton
              text="Complete"
              width={100}
              color={Theme.palette.primary.main}
              onClick={handleComplete}
            />
          )}

          {quest.completed && (
            <Typography
              color="#0f0"
              fontWeight="bold"
              style={{ marginTop: 4, textAlign: "center" }}
            >
              Reward Sent!
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal open={visible} onClose={onClose} closeAfterTransition>
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={backdropStyle}
            >
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={modalWrapperStyle}
              >
                <Box sx={modalContentStyle}>
                  {/* Title */}
                  <Typography
                    color="#fff"
                    fontWeight="bold"
                    variant="h5"
                    align="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    My Quests
                  </Typography>
                  {/* Content */}

                  <Box sx={scrollAreaStyle}>
                    {activeQuests.filter((q) => !q.completed).map(renderQuest)}
                  </Box>

                  {/* Close Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-2%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close />}
                      color={Theme.palette.secondary.main}
                      onClick={onClose}
                    />
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>
    </>
  );
}
